/**
 * Simplified Supabase Query Hook for React SPA
 * 
 * React Query와 Supabase를 통합한 간소화된 쿼리 훅
 * - ERR_CONNECTION_CLOSED 감지 시 페이지 리로드
 * - AbortError 처리 유지
 * - 간단하고 안정적인 에러 처리
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { PostgrestError } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/lib/supabase';
import { STALE_TIMES } from '@/lib/query-client';
import { useEffect, useState } from 'react';

/**
 * Simplified error handling for Supabase operations
 */
export class SupabaseQueryError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly details?: string,
    public readonly hint?: string,
    public readonly originalError?: PostgrestError | Error,
    public readonly isAborted?: boolean
  ) {
    super(message);
    this.name = 'SupabaseQueryError';
  }

  static fromPostgrestError(error: PostgrestError, operation: string): SupabaseQueryError {
    return new SupabaseQueryError(
      `${operation} failed: ${error.message}`,
      error.code,
      error.details,
      error.hint,
      error
    );
  }

  static fromError(error: Error | unknown, operation: string): SupabaseQueryError {
    if (error instanceof SupabaseQueryError) {
      return error;
    }
    
    if (error instanceof Error) {
      // Handle AbortError gracefully - these are expected
      const isAbortError = error.name === 'AbortError' || error.message?.includes('aborted');
      
      if (isAbortError) {
        console.log(`⏹️ ${operation} was cancelled (AbortError) - expected behavior`);
        return new SupabaseQueryError(
          `${operation} was cancelled`,
          'ABORT_ERROR',
          'Request was aborted by browser or user navigation',
          'This is normal behavior when switching tabs or during page transitions',
          error,
          true // Mark as aborted
        );
      }
      
      // Check for critical connection errors that require page reload
      const errorMessage = error.message || '';
      if (errorMessage.includes('ERR_CONNECTION_CLOSED')) {
        console.warn(`🔄 ${operation} detected ERR_CONNECTION_CLOSED - reloading page`);
        // Trigger page reload
        setTimeout(() => window.location.reload(), 500);
        return new SupabaseQueryError(
          `${operation} failed: Connection closed, page will reload`,
          'ERR_CONNECTION_CLOSED',
          error.message,
          'Page will reload to restore connection',
          error,
          false
        );
      }
      
      return new SupabaseQueryError(
        `${operation} failed: ${error.message}`,
        undefined,
        undefined,
        undefined,
        error
      );
    }
    
    return new SupabaseQueryError(
      `${operation} failed: Unknown error`,
      undefined,
      undefined,
      undefined,
      new Error(String(error))
    );
  }

  /**
   * Helper method to check if this error should be treated as non-critical
   */
  isNonCritical(): boolean {
    return this.isAborted || false;
  }
}

/**
 * Standard query options for Supabase queries
 */
export interface SupabaseQueryOptions<T> extends Omit<UseQueryOptions<T, SupabaseQueryError>, 'queryFn' | 'queryKey'> {
  /**
   * Timeout in milliseconds (default: 10000)
   */
  timeout?: number;
  
  /**
   * Enable debug logging
   */
  debug?: boolean;
  
  /**
   * Custom error handler
   */
  onError?: (error: SupabaseQueryError) => void;

  /**
   * 데이터 유형별 staleTime 설정
   * 'static' | 'semi_static' | 'dynamic' | 'real_time' | 'auth' 또는 사용자 정의 milliseconds
   */
  cacheType?: 'static' | 'semi_static' | 'dynamic' | 'real_time' | 'auth' | number;
}

/**
 * Standard mutation options for Supabase mutations
 */
export interface SupabaseMutationOptions<TData, TVariables> 
  extends Omit<UseMutationOptions<TData, SupabaseQueryError, TVariables>, 'mutationFn'> {
  /**
   * Timeout in milliseconds (default: 10000)
   */
  timeout?: number;
  
  /**
   * Enable debug logging
   */
  debug?: boolean;
}

/**
 * Simple tab visibility detection hook
 */
export function useTabVisibility() {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}

/**
 * Smart polling interval that pauses when tab is in background
 */
export function useSmartInterval(interval: number | false | undefined) {
  const isTabVisible = useTabVisibility();
  
  // Return false (no polling) when tab is not visible or interval is disabled
  if (!isTabVisible || !interval) {
    return false;
  }
  
  return interval;
}

/**
 * Standard Supabase query hook
 */
export function useSupabaseQuery<T>(
  queryKey: readonly unknown[],
  queryFn: (options: { signal?: AbortSignal }) => Promise<T>,
  options?: SupabaseQueryOptions<T>
) {
  const {
    debug = false,
    onError,
    cacheType = 'dynamic',
    ...reactQueryOptions
  } = options || {};

  // cacheType에 따른 staleTime 설정
  const getStaleTime = (type: SupabaseQueryOptions<T>['cacheType']): number => {
    if (typeof type === 'number') {
      return type;
    }
    
    switch (type) {
      case 'static':
        return STALE_TIMES.STATIC;
      case 'semi_static':
        return STALE_TIMES.SEMI_STATIC;
      case 'real_time':
        return STALE_TIMES.REAL_TIME;
      case 'auth':
        return STALE_TIMES.AUTH;
      case 'dynamic':
      default:
        return STALE_TIMES.DYNAMIC;
    }
  };

  const queryFnWrapper = async ({ signal }: { signal?: AbortSignal }) => {
    // queryKey가 유효한지 확인
    const safeQueryKey = Array.isArray(queryKey) ? queryKey : [];
    const operation = `Query [${safeQueryKey.map(k => String(k)).join(', ')}]`;
    
    if (debug) {
      console.log(`🔍 ${operation} starting...`);
    }

    try {
      const result = await queryFn({ signal });
      
      if (debug) {
        console.log(`✅ ${operation} completed successfully`);
      }
      return result;
    } catch (error) {
      const supabaseError = SupabaseQueryError.fromError(error, operation);
      
      // Handle errors appropriately
      if (supabaseError.isNonCritical()) {
        if (debug) {
          console.log(`⏹️ ${operation} cancelled (expected behavior)`);
        }
      } else {
        if (debug) {
          console.error(`❌ ${operation} failed:`, supabaseError);
        }
      }
      
      if (onError) {
        onError(supabaseError);
      }
      
      throw supabaseError;
    }
  };

  return useQuery<T, SupabaseQueryError>({
    queryKey,
    queryFn: queryFnWrapper,
    
    // 표준화된 staleTime 적용 (사용자 지정이 없는 경우)
    staleTime: reactQueryOptions.staleTime ?? getStaleTime(cacheType),
    
    // Simple retry logic
    retry: (failureCount, error) => {
      if (error instanceof SupabaseQueryError) {
        // Never retry AbortErrors - they're intentional cancellations
        if (error.isNonCritical()) {
          return false;
        }
        
        // Never retry connection errors - page reload will handle them
        if (error.code === 'ERR_CONNECTION_CLOSED') {
          return false;
        }
      }
      
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    
    // Standard retry delay
    retryDelay: (attemptIndex) => {
      return Math.min(1000 * Math.pow(2, attemptIndex), 5000);
    },
    ...reactQueryOptions,
  });
}

/**
 * Standard Supabase mutation hook
 */
export function useSupabaseMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: SupabaseMutationOptions<TData, TVariables>
) {
  const {
    debug = false,
    onError,
    ...reactMutationOptions
  } = options || {};

  const mutationFnWrapper = async (variables: TVariables) => {
    const operation = 'Mutation';
    if (debug) {
      console.log(`🔄 ${operation} starting...`, variables);
    }

    try {
      const result = await mutationFn(variables);
      if (debug) {
        console.log(`✅ ${operation} completed successfully`);
      }
      return result;
    } catch (error) {
      const supabaseError = SupabaseQueryError.fromError(error, operation);
      if (debug) {
        console.error(`❌ ${operation} failed:`, supabaseError);
      }
      throw supabaseError;
    }
  };

  return useMutation<TData, SupabaseQueryError, TVariables>({
    mutationFn: mutationFnWrapper,
    onError,
    ...reactMutationOptions,
  });
}

/**
 * Invalidate queries helper
 */
export function useInvalidateQueries() {
  const queryClient = useQueryClient();
  
  return {
    invalidate: (queryKey: readonly unknown[]) => {
      return queryClient.invalidateQueries({ queryKey });
    },
    invalidateAll: () => {
      return queryClient.invalidateQueries();
    },
    remove: (queryKey: readonly unknown[]) => {
      return queryClient.removeQueries({ queryKey });
    }
  };
}

/**
 * Simple connection status helper
 */
export function useConnectionStatus() {
  const supabase = getSupabaseClient();
  const isTabVisible = useTabVisibility();
  
  return useQuery({
    queryKey: ['connection-status'],
    queryFn: async ({ signal }) => {
      try {
        if (signal?.aborted) {
          throw new Error('Connection check was cancelled');
        }

        const query = supabase
          .from('profiles')
          .select('id')
          .limit(1);
        
        // Only add abortSignal if signal is provided
        if (signal) {
          query.abortSignal(signal);
        }
        
        const { error: dbError } = await query;
          
        if (dbError) {
          // Check for ERR_CONNECTION_CLOSED and reload page
          if (dbError.message?.includes('ERR_CONNECTION_CLOSED')) {
            console.warn('🔄 ERR_CONNECTION_CLOSED detected - reloading page');
            setTimeout(() => window.location.reload(), 500);
          }
          
          return { connected: false, error: dbError, details: 'database_error' };
        }
        
        return { connected: true, error: null, details: 'healthy' };
        
      } catch (error) {
        if (error instanceof Error && 
            (error.name === 'AbortError' || error.message?.includes('aborted'))) {
          throw error;
        }
        
        // Check for ERR_CONNECTION_CLOSED in generic errors
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('ERR_CONNECTION_CLOSED')) {
          console.warn('🔄 ERR_CONNECTION_CLOSED detected - reloading page');
          setTimeout(() => window.location.reload(), 500);
        }
        
        return { 
          connected: false, 
          error: error instanceof Error ? error : new Error('Connection check failed'),
          details: 'connection_check_failed'
        };
      }
    },
    staleTime: STALE_TIMES.REAL_TIME, // 30초 - 실시간 연결 상태 모니터링
    gcTime: 5 * 60 * 1000,
    refetchInterval: isTabVisible ? STALE_TIMES.REAL_TIME : false,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      if (error instanceof Error && 
          (error.name === 'AbortError' || error.message?.includes('aborted'))) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchOnMount: true,
    useErrorBoundary: false,
  });
}